<template>
  <div class="profile-page">
    <div class="profile-bg" aria-hidden="true">
      <div class="profile-orb profile-orb-1"></div>
      <div class="profile-orb profile-orb-2"></div>
      <div class="profile-orb profile-orb-3"></div>
    </div>

    <desktop-brand-corner />

    <button type="button" class="mobile-back-btn" @click="handleGoBack" aria-label="返回上一页">
      <font-awesome-icon icon="chevron-left" />
    </button>

    <main class="profile-main">
      <section class="profile-card">
        <header class="profile-header">
          <span class="profile-icon">
            <font-awesome-icon icon="seedling" />
          </span>
          <h1 class="profile-title">我的账号</h1>
        </header>

        <div class="profile-fields">
          <div class="profile-field">
            <label class="profile-label" for="profile-username">用户名</label>
            <input
              id="profile-username"
              class="profile-input profile-input-muted"
              type="text"
              :value="userInfo.username"
              readonly
            />
          </div>

          <div class="profile-field">
            <label class="profile-label" for="profile-email">邮箱</label>
            <input
              id="profile-email"
              class="profile-input"
              type="email"
              :value="userInfo.email"
              readonly
            />
          </div>
        </div>

        <div class="profile-divider"></div>

        <button type="button" class="profile-logout-btn" @click="handleLogout">
          登出账号
        </button>
      </section>
    </main>
  </div>
</template>

<script setup>
import { onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import * as UserApi from '@/api/user'
import { removeToken } from '@/config/axios/auth'

const router = useRouter()

const userInfo = ref({
  username: '',
  email: ''
})

const handleGoBack = () => {
  router.push({
    path: '/',
    query: {
      tab: 'profile',
    },
  })
}

const getUser = async () => {
  try {
    const { username, email } = await UserApi.getUser()
    userInfo.value.username = username || ''
    userInfo.value.email = email || ''
  } catch {
    // 接口错误已在 axios 拦截器中统一提示
  }
}

const handleLogout = async () => {
  try {
    await UserApi.logout()
  } finally {
    removeToken()
    window.sessionStorage.removeItem('tree-sprout-parent-verified')
    window.sessionStorage.removeItem('vipTag')
    router.replace('/login')
  }
}

onMounted(() => {
  getUser()
})
</script>

<style scoped>
.profile-page {
  min-height: 100vh;
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #fdfbf7 0%, #f5f0e8 50%, #faf8f5 100%);
  padding: 20px;
}

.profile-bg {
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
}

.profile-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.5;
  animation: float-orb 8s ease-in-out infinite;
}

.profile-orb-1 {
  width: 400px;
  height: 400px;
  background: linear-gradient(135deg, #ffe4d4 0%, #ffd4c4 100%);
  top: -100px;
  right: -100px;
  animation-delay: 0s;
}

.profile-orb-2 {
  width: 350px;
  height: 350px;
  background: linear-gradient(135deg, #e8f4ff 0%, #d4e8ff 100%);
  bottom: -50px;
  left: -100px;
  animation-delay: -3s;
}

.profile-orb-3 {
  width: 250px;
  height: 250px;
  background: linear-gradient(135deg, #ffeaa8 0%, #ffd88a 100%);
  top: 50%;
  left: 30%;
  animation-delay: -5s;
}

@keyframes float-orb {
  0%,
  100% {
    transform: translate(0, 0) scale(1);
  }

  33% {
    transform: translate(30px, -30px) scale(1.05);
  }

  66% {
    transform: translate(-20px, 20px) scale(0.95);
  }
}

.profile-main {
  position: relative;
  z-index: 1;
  min-height: calc(100vh - 40px);
  display: flex;
  align-items: center;
  justify-content: center;
}

.profile-card {
  width: min(100%, 520px);
  padding: 42px 40px 40px;
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: 32px;
  border: 1px solid rgba(255, 255, 255, 0.82);
  box-shadow:
    0 25px 50px -12px rgba(0, 0, 0, 0.08),
    0 0 0 1px rgba(255, 255, 255, 0.45) inset;
}

.profile-header {
  display: flex;
  align-items: center;
  gap: 16px;
  text-align: left;
  margin-bottom: 28px;
}

.profile-icon {
  width: 40px;
  height: 40px;
  margin: 0;
  border-radius: 50%;
  background: linear-gradient(135deg, #fff5e8 0%, #ffd8ad 100%);
  color: #d97721;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  box-shadow:
    0 8px 16px rgba(214, 126, 45, 0.18),
    inset 0 0 0 1px rgba(255, 255, 255, 0.8);
}

.profile-title {
  margin: 0;
  color: #111827;
  font-size: 24px;
  font-weight: 900;
  line-height: 1.2;
}

.profile-fields {
  display: grid;
  gap: 24px;
}

.profile-field {
  display: grid;
  gap: 10px;
}

.profile-label {
  color: #4f4137;
  font-size: 16px;
  font-weight: 700;
}

.profile-input {
  width: 100%;
  min-height: 72px;
  box-sizing: border-box;
  border: none;
  border-radius: 22px;
  background: rgba(249, 244, 236, 0.88);
  color: #352c26;
  padding: 0 20px;
  font-size: 18px;
  font-weight: 500;
  outline: none;
  box-shadow: inset 0 0 0 1px rgba(201, 177, 151, 0.34);
}

.profile-input-muted {
  background: rgba(249, 244, 236, 0.88);
}

.profile-divider {
  height: 1px;
  margin: 28px 0 24px;
  background: rgba(201, 177, 151, 0.34);
}

.profile-logout-btn {
  width: 100%;
  min-height: 78px;
  margin-top: 8px;
  border: none;
  border-radius: 16px;
  background: linear-gradient(135deg, #ff8a3d 0%, #ff6b35 100%);
  color: #fff;
  font-size: 17px;
  font-weight: 800;
  letter-spacing: 4px;
  cursor: pointer;
  box-shadow: 0 8px 24px rgba(255, 138, 61, 0.35);
  transition:
    transform 0.25s ease,
    box-shadow 0.25s ease,
    background 0.25s ease,
    color 0.25s ease;
}

.profile-logout-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 32px rgba(255, 138, 61, 0.5);
}

.profile-logout-btn:active {
  transform: scale(0.99);
}

.mobile-back-btn {
  display: none;
}

@media (max-width: 767px) {
  .mobile-back-btn {
    position: absolute;
    top: calc(env(safe-area-inset-top, 0px) + 16px);
    left: 16px;
    z-index: 2;
    display: inline-flex;
    width: 44px;
    height: 44px;
    border: 1px solid rgba(255, 255, 255, 0.85);
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.82);
    backdrop-filter: blur(14px);
    -webkit-backdrop-filter: blur(14px);
    color: #5f5044;
    font-size: 16px;
    box-shadow:
      0 8px 24px rgba(0, 0, 0, 0.06),
      inset 0 1px 0 rgba(255, 255, 255, 0.78);
    cursor: pointer;
    align-items: center;
    justify-content: center;
  }
}

@media (max-width: 560px) {
  .profile-page {
    padding: 16px;
  }

  .profile-main {
    min-height: calc(100vh - 32px);
  }

  .profile-card {
    padding: 34px 24px 30px;
    border-radius: 26px;
  }

  .profile-title {
    font-size: 23px;
  }

  .profile-input {
    min-height: 64px;
    border-radius: 20px;
    font-size: 16px;
  }

  .profile-logout-btn {
    min-height: 56px;
    font-size: 17px;
  }
}

@media (max-width: 768px),
  (min-width: 769px) and (max-width: 1024px) and (orientation: portrait),
  (hover: none) and (pointer: coarse) and (orientation: landscape) and (max-height: 540px) {
  .profile-orb {
    display: none;
  }

  .profile-card,
  .mobile-back-btn {
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
  }

  .profile-logout-btn,
  .mobile-back-btn {
    transition-property: background-color, color, border-color, box-shadow, transform, opacity;
  }
}
</style>
